
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { Profile } from '../types/profileTypes';

export const useProfileData = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user, t]);

  const fetchProfile = async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      
      const { data: existingProfile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      
      if (profileError) {
        console.error("Error fetching profile:", profileError);
        toast.error(t('errors.profileLoadFailed'));
        return;
      }
      
      if (existingProfile) {
        // Cast to ensure type safety with social_links
        const profileData: Profile = {
          ...existingProfile,
          social_links: existingProfile.social_links as Record<string, string> | null
        };
        setProfile(profileData);
      } else {
        const defaultProfile: Profile = {
          id: user.id,
          username: user.email?.split('@')[0] || 'user',
          full_name: user.user_metadata?.full_name || 'New Creator',
          avatar_url: user.user_metadata?.avatar_url || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop",
          expression_types: ['edit'],
          bio: null,
          social_links: null,
          role: 'creator'
        };
        
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles')
          .insert([defaultProfile])
          .select()
          .single();
        
        if (insertError) {
          console.error("Error creating profile:", insertError);
          toast.error(t('errors.profileCreateFailed'));
        } else {
          // Cast to ensure type safety with social_links
          const newProfileData: Profile = {
            ...newProfile,
            social_links: newProfile.social_links as Record<string, string> | null
          };
          setProfile(newProfileData);
          toast.success(t('profile.createdNew'));
        }
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      toast.error(t('errors.unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    profile,
    isLoading,
    refetchProfile: fetchProfile
  };
};
